import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview FeedEventRow — LOCI horizontal event row.
 *
 * 92px square cover on the left, title + venue + mono time/distance in the
 * middle, amber Heart toggle and mono price on the right. Used inside
 * `NeighborhoodGroup` lists on the Home surface.
 */
import { forwardRef, useCallback } from 'react';
import { useTheme } from '../../../theme';
import { Heart } from '../Heart';
const COVER_SIZE_PX = {
    sm: 64,
    md: 92,
};
/**
 * `<FeedEventRow>` — horizontal event row used inside `NeighborhoodGroup`.
 *
 * Rendered as `role="button"` on a div (not a native `<button>`) so the
 * inner Heart can remain a real `<button>` without nesting — both controls
 * are still keyboard-reachable via Tab, and Enter/Space on the row triggers
 * `onOpen`. Both controls expose sentence-case `aria-label`s.
 */
export const FeedEventRow = forwardRef(({ id, title, venue, time, distance, price, imageUrl, coverSize = 'md', saved, onToggleSave, onOpen, className, style, 'data-testid': dataTestId = 'feed-event-row', }, ref) => {
    const theme = useTheme();
    const coverDimension = COVER_SIZE_PX[coverSize];
    const handleOpen = useCallback(() => {
        onOpen(id);
    }, [id, onOpen]);
    const handleRowKeyDown = useCallback((event) => {
        if (event.target !== event.currentTarget)
            return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(id);
        }
    }, [id, onOpen]);
    const handleHeartClick = useCallback((event) => {
        event.stopPropagation();
        onToggleSave(id, !saved);
    }, [id, onToggleSave, saved]);
    // Keyboard focus treatment: rely on the default user-agent focus ring so
    // keyboard-only users always see focus location on both the row and the
    // Heart button (no custom `outline: 'none'` here). Rounded corners on the
    // focus ring are provided via `borderRadius` below.
    const rowStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        width: '100%',
        padding: `${theme.spacing.sm} ${theme.spacing.xs}`,
        background: 'transparent',
        border: 'none',
        borderRadius: theme.borderRadius.sm,
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
    };
    const coverStyle = {
        width: coverDimension,
        height: coverDimension,
        flexShrink: 0,
        borderRadius: theme.borderRadius.sm,
        background: imageUrl
            ? `center / cover no-repeat url(${JSON.stringify(imageUrl)})`
            : theme.colors.surfaceAlt,
        backgroundColor: theme.colors.surfaceAlt,
    };
    const bodyStyle = {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    };
    const titleStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.body,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.005em',
    };
    const venueStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
        color: theme.colors.textMuted,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
    const metaStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '12px',
        letterSpacing: '0.04em',
        color: theme.colors.textMuted,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    const trailStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        flexShrink: 0,
    };
    const heartButtonStyle = {
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        // 22px Heart + 11px padding × 2 = 44px hit area — meets the 44px mobile
        // touch-target minimum without inflating the icon.
        padding: 11,
        margin: 0,
        cursor: 'pointer',
        color: theme.colors.accent,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.borderRadius.sm,
    };
    const priceStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: theme.typography.fontSize.small,
        color: theme.colors.text,
        minWidth: 48,
        textAlign: 'right',
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    const focusRingClasses = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
    const rowClassName = [focusRingClasses, className].filter(Boolean).join(' ');
    return (_jsxs("div", { ref: ref, role: "button", tabIndex: 0, onClick: handleOpen, onKeyDown: handleRowKeyDown, "data-testid": dataTestId, "data-event-id": id, "aria-label": `Open ${title} at ${venue}`, className: rowClassName, style: { ...rowStyle, ...style }, children: [_jsx("span", { "aria-hidden": "true", style: coverStyle, "data-testid": "feed-event-row-cover" }), _jsxs("span", { style: bodyStyle, children: [_jsx("span", { style: titleStyle, children: title }), _jsx("span", { style: venueStyle, children: venue }), _jsxs("span", { style: metaStyle, "data-testid": "feed-event-row-meta", children: [time, " \u00B7 ", distance] })] }), _jsxs("span", { style: trailStyle, children: [_jsx("button", { type: "button", onClick: handleHeartClick, "aria-label": saved ? `Remove ${title} from saved` : `Save ${title}`, "aria-pressed": saved, "data-testid": "feed-event-row-heart", style: heartButtonStyle, children: _jsx(Heart, { filled: saved, color: theme.colors.accent, size: 22 }) }), _jsx("span", { style: priceStyle, "data-testid": "feed-event-row-price", children: price })] })] }));
});
FeedEventRow.displayName = 'FeedEventRow';
